import React, { createContext, useContext, useState } from "react";
import { Customer } from "../types/customer";
import {
  createCustomer,
  deleteCustomerApi,
  getCustomers,
  updateCustomerApi,
} from "../api/customerApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface CustomerContextProps {
  customers: Customer[];
  addCustomer: (customer: Customer) => Promise<void>;
  updateCustomer: (customer: Customer) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;
  fetchCustomers: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextProps | undefined>(undefined);

export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);

  const fetchCustomers = async () => {
    const list = await handleWithAlert(
      () => getCustomers(),
      {
        errorMessage: "Failed to fetch customer list",
      }
    );
    if (list) {
      setCustomers(list.map((c: any) => ({
        ...c,
        id: c._id || c.id // ensure 'id' is present for frontend
      })));
    }
  };

  const addCustomer = async (customer: Customer) => {
    const created = await handleWithAlert(
      () => createCustomer(customer),
      {
        successMessage: "Customer created successfully!",
        errorMessage: "Failed to create customer",
        showSuccess: true,
      }
    );
    if (created) {
      fetchCustomers();
    }
  };

  const updateCustomer = async (updatedCustomer: Customer) => {
    const updated = await handleWithAlert(
      () => updateCustomerApi(updatedCustomer.id!, updatedCustomer),
      {
        successMessage: "Customer updated successfully!",
        errorMessage: "Failed to update customer",
        showSuccess: true,
      }
    );
    if (updated) {
      fetchCustomers();
    }
  };

  const deleteCustomer = async (id: string) => {
    const deleted = await handleWithAlert(
      () => deleteCustomerApi(id),
      {
        successMessage: "Customer deleted successfully!",
        errorMessage: "Failed to delete customer",
        showSuccess: true,
      }
    );
    if (deleted !== null) {
      setCustomers(prev => prev.filter(c => c.id !== id));
    }
  };

  return (
    <CustomerContext.Provider value={{
      customers,
      addCustomer,
      updateCustomer,
      deleteCustomer,
      fetchCustomers
    }}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) throw new Error("useCustomer must be inside CustomerProvider");
  return context;
};
